import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type MeResponse } from '../api/client'
import { resolveTheme } from '../lib/theme'
import { ThemeSwitch } from './ThemeSwitch'
import { titleCards } from './titles'
import { deskHref, manualHref } from './links'
import { deskPubsOf, deskTabsOf } from '../lib/deskAccess'
import { pubLabel } from '../lib/deskFormat'
import { deskPath } from '../lib/deskTabs'
import { ridesHref } from './rides/ridesApi'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'

/** The Editorial Desk shell: its own full-screen area, outside the
 *  console's NavRail. It sets data-theme so the light/dark tokens apply here only. */
export function EditorialLayout() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const [systemDark, setSystemDark] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq?.addEventListener) return
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  const pref = me?.preferences?.editorial_theme ?? 'light'
  const theme = resolveTheme(pref, systemDark)
  // Mirror the theme on <html> so portals inherit it; the console stays dark once we leave.
  useEffect(() => {
    if (!me) return
    document.documentElement.dataset.theme = theme
    return () => { delete document.documentElement.dataset.theme }
  }, [me, theme])
  if (!me) return null
  const cards = titleCards(me.sections ?? [], me.role === 'admin')

  return (
    <div data-theme={theme} className="min-h-screen bg-ink-900 text-paper font-ui text-[14px] md:flex">
      <nav aria-label="Editorial Desk" className="flex w-full shrink-0 flex-col gap-6 border-b border-ink-500 bg-ink-700 px-3.5 pb-7 pt-6 md:min-h-screen md:w-[264px] md:border-b-0 md:border-r">
        <div className="flex flex-col gap-0.5 px-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-glow">AAMCirc</span>
          <span className="font-serif text-[23px] font-medium tracking-tight">Editorial Desk</span>
        </div>

        <div className="flex flex-col gap-1">
          <NavLink to="/editorial" end className={({ isActive }) => `px-2 pb-1 text-[11px] font-bold uppercase tracking-[0.1em] hover:text-paper ${focus} ${isActive ? 'text-paper' : 'text-paper-faint'}`}>
            Titles
          </NavLink>
          {cards.map((t) =>
            t.state === 'open' ? (
              <NavLink
                key={t.slug}
                to={deskHref(t.path)}
                className={({ isActive }) => `flex items-center justify-between gap-2 rounded-lg px-2.5 py-[9px] text-paper ${focus} ${isActive ? 'bg-ink-600 ring-1 ring-ink-400' : 'hover:bg-ink-600'}`}
              >
                <span className="flex min-w-0 flex-col gap-px">
                  <span className="font-semibold">{t.title}</span>
                  <span className="text-[12px] text-paper-faint">{t.section}{t.members ? ` · ${t.members}` : ''}</span>
                </span>
                {t.switchedOn === false && <span className="whitespace-nowrap rounded-full bg-ink-600 px-2 py-[3px] text-[11px] font-semibold text-paper-dim">Not switched on</span>}
              </NavLink>
            ) : (
              <div key={t.slug} className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-[9px] text-paper-faint">
                <span className="flex min-w-0 flex-col gap-px">
                  <span className="font-semibold">{t.title}</span>
                  <span className="text-[12px]">{t.section}</span>
                </span>
                <span className="whitespace-nowrap rounded-full bg-ink-600 px-2 py-[3px] text-[11px] font-semibold text-paper-dim">Not set up yet</span>
              </div>
            ),
          )}
        </div>

        {(me.sections ?? []).some((s) => s.readers_rides) && (
          <div className="flex flex-col gap-1">
            <span className="px-2 pb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-paper-faint">Readers Rides</span>
            {(me.sections ?? []).filter((s) => s.readers_rides).map((s) => (
              <NavLink key={s.slug} to={ridesHref(s.path)} className={({ isActive }) => `rounded-lg px-2.5 py-[9px] font-semibold text-paper ${focus} ${isActive ? 'bg-ink-600 ring-1 ring-ink-400' : 'hover:bg-ink-600'}`}>
                Readers Rides{(me.sections ?? []).filter((x) => x.readers_rides).length > 1 ? ` · ${s.label}` : ''}
              </NavLink>
            ))}
          </div>
        )}

        {me.role !== 'admin' && deskTabsOf(me).includes('articles') && deskPubsOf(me).filter((p) => p !== 'all').length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="px-2 pb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-paper-faint">Readers</span>
            {deskPubsOf(me).filter((p) => p !== 'all').map((p) => (
              <NavLink key={p} to={deskPath(p, 'articles')} className={({ isActive }) => `rounded-lg px-2.5 py-[9px] font-semibold text-paper ${focus} ${isActive ? 'bg-ink-600 ring-1 ring-ink-400' : 'hover:bg-ink-600'}`}>
                Article &amp; issue stats · {pubLabel(p)}
              </NavLink>
            ))}
          </div>
        )}

        <div className="mt-auto flex flex-col gap-3 border-t border-ink-500 px-2.5 pt-3.5 text-[13px]">
          <span className="text-paper-faint">Signed in as <strong className="break-all font-semibold text-paper">{me.email}</strong></span>
          <ThemeSwitch value={pref} />
          <NavLink to={manualHref()} className={({ isActive }) => `underline underline-offset-2 hover:text-paper ${focus} ${isActive ? 'text-paper' : 'text-amber-glow'}`}>User manual</NavLink>
          {me.role !== 'editor' && me.role !== 'writer' && (
            <NavLink to="/" className={`text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>Back to the AAMCirc console</NavLink>
          )}
        </div>
      </nav>
      <main className="min-w-0 flex-1">
        <Outlet />
      </main>
    </div>
  )
}
